import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "Social Media Strategy",
    description:
      "Planning built around audience, goals, message, and the role social media should play for the business.",
  },
  {
    icon: Layers,
    title: "Content Direction",
    description:
      "Clear content themes, post ideas, and structured messaging that support consistency.",
  },
  {
    icon: Code,
    title: "Content Creation Support",
    description:
      "Support for branded post copy, creative direction, and campaign-ready content planning.",
  },
  {
    icon: TrendingUp,
    title: "Paid Social Advertising",
    description:
      "Targeted paid campaign support where social media needs stronger reach or faster traction.",
  },
  {
    icon: Smartphone,
    title: "Community Presence",
    description:
      "Support for building a more active and credible brand presence online.",
  },
  {
    icon: Zap,
    title: "Performance Review",
    description:
      "Content and campaign review based on what is working, what is missing, and what should improve.",
  },
];

const offerings = [
  {
    title: "Brand Content Strategy",
    description:
      "Content direction that helps your business show up more clearly and consistently.",
  },
  {
    title: "Platform-Specific Content Planning",
    description:
      "Content structured around the platforms your audience is most likely to use.",
  },
  {
    title: "Paid Campaign Support",
    description:
      "Paid social planning for offers, awareness, or targeted audience growth.",
  },
  {
    title: "Business-Focused Social Direction",
    description:
      "Social media support that works alongside your website, SEO, and broader digital presence.",
  },
];

const deliverables = [
  "Content direction based on business goals",
  "Brand messaging guidance for social posts",
  "Content themes and planning structure",
  "Paid social campaign input where needed",
  "Creative direction support",
  "Posting and visibility recommendations",
  "Content review and refinement",
  "Clear next-step priorities",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We learn about your business, audience, brand direction, and what social media needs to support.",
  },
  {
    step: "02",
    title: "Strategy",
    description:
      "We define the message, content focus, platform priorities, and overall direction.",
  },
  {
    step: "03",
    title: "Planning",
    description:
      "We organize themes, content types, and campaign opportunities around the business goals.",
  },
  {
    step: "04",
    title: "Content Direction",
    description:
      "We shape what should be posted, how it should sound, and how it should support trust and visibility.",
  },
  {
    step: "05",
    title: "Review",
    description:
      "We assess what is working, where the message is landing, and what should improve.",
  },
  {
    step: "06",
    title: "Refine & Support",
    description:
      "We continue improving direction, consistency, and campaign support over time.",
  },
];

const faqs = [
  {
    question: "What is included in your social media marketing service?",
    answer: (
      <>
        The service can include strategy, content direction, content planning,
        creative guidance, paid social support, and performance review
        depending on what the business needs.
      </>
    ),
    schemaAnswer:
      "The service can include strategy, content direction, content planning, creative guidance, paid social support, and performance review depending on what the business needs.",
  },
  {
    question: "Do you create the content too?",
    answer: (
      <>
        We can support content direction, messaging, and creative planning. The
        exact content creation scope depends on the project setup.
      </>
    ),
    schemaAnswer:
      "We can support content direction, messaging, and creative planning. The exact content creation scope depends on the project setup.",
  },
  {
    question: "Do you also manage paid ads?",
    answer: (
      <>
        We can support paid social advertising where it fits the business goal
        and message clearly.
      </>
    ),
    schemaAnswer:
      "We can support paid social advertising where it fits the business goal and message clearly.",
  },
  {
    question: "Which platforms do you work with?",
    answer: (
      <>
        That depends on where your audience is most active and what role social
        media should play in your wider{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          digital strategy
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "That depends on where your audience is most active and what role social media should play in your wider digital strategy.",
  },
  {
    question: "Will social media help if my website is weak?",
    answer: (
      <>
        Social media can improve visibility, but it works best when the{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website and core digital presence
        </Link>{" "}
        also support conversion and trust.
      </>
    ),
    schemaAnswer:
      "Social media can improve visibility, but it works best when the website and core digital presence also support conversion and trust.",
  },
  {
    question: "How do you decide what content to post?",
    answer: (
      <>
        We look at the business goals, audience questions, content
        opportunities, and the kind of message that will support visibility and
        trust.
      </>
    ),
    schemaAnswer:
      "We look at the business goals, audience questions, content opportunities, and the kind of message that will support visibility and trust.",
  },
];

const whyChooseUs = [
  {
    title: "Strategy Before Content",
    description:
      "We define the audience, message, and goals before planning what gets posted.",
  },
  {
    title: "Built Around the Business",
    description:
      "The content direction is shaped around your business model, audience, and actual priorities.",
  },
  {
    title: "Organic + Paid Thinking",
    description:
      "We can support both consistent brand content and paid social campaigns where they make sense.",
  },
  {
    title: "Clearer Content Direction",
    description:
      "The goal is not more noise. The goal is stronger visibility with a clearer message.",
  },
];

export default function SocialMediaMarketingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
  title="Best Social Media Management Agency | Square Trigger"
  description="Get Social Media Marketing services, including paid advertising strategies, to be ahead of your competitors. Establish a strong, recognizable presence today."
  ogTitle="Best Social Media Management Agency | Square Trigger"
  ogDescription="Get Social Media Marketing services, including paid advertising strategies, to be ahead of your competitors. Establish a strong, recognizable presence today."
  ogSiteName="Square Trigger Social Media Marketing services"
  ogImage="https://squaretrigger.com/social.webp"
  keywords={SEO_KEYWORDS.socialMediaMarketing}
/>

     {/* Hero Section */}
<section className="relative overflow-hidden">
 
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
    style={{ backgroundImage: "url('/social.webp')" }}
  />

  {/* Overlay for mobile + desktop */}
  <div className="absolute inset-0 bg-gradient-to-r from-foreground/72 via-foreground/58 to-foreground/38 lg:bg-gradient-to-br lg:from-foreground lg:via-foreground/95 lg:to-foreground/90" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-96 sm:w-96" />
          <div className="absolute bottom-16 left-6 h-56 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:h-72 sm:w-72" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Our Services</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Social Media Marketing That Helps Your Brand Show Up and{" "}
                <span className="gradient-text">Build Trust</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We create social media strategies, content direction, and paid
                social support that help businesses stay visible, build
                credibility, and connect with the right audience more
                consistently.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We help your brand stay active with clearer messaging, stronger
                consistency, and content that supports awareness, engagement, and
                long-term trust.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/work"
                    className="flex items-center justify-center gap-2"
                  >
                    View Our Work
                  </Link>
                </Button>
              </motion.div>
            </div>

            <div className="hidden min-h-[420px] items-center justify-center lg:flex">
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay: 0.4 }}
    className="relative flex items-center justify-center"
  >
    
    <div className="relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <img 
        src="/social.webp" 
        alt="Digital Agency Vancouver" 
        className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full max-w-[520px] h-auto"
      />
    </div>
  </motion.div>
</div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
              Visibility Builds Familiarity. Familiarity Builds{" "}
              <span className="gradient-text">Trust.</span>
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              For many businesses, social media shapes the first impression long
              before a call, form submission, or sale. Consistent, useful
              content helps people understand your brand, recognize your value,
              and remember you when they are ready to take action.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8"
            >
              <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Good social media marketing is not just posting for the sake of
                posting. It is about showing up clearly, speaking to the right
                audience, and supporting the wider goals of the business.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
                {[
                  {
                    title: "Consistent visibility",
                    desc: "Stay visible to your audience instead of disappearing between campaigns.",
                  },
                  {
                    title: "Stronger brand trust",
                    desc: "Clear and useful content helps people feel more confident in your business.",
                  },
                  {
                    title: "Better message control",
                    desc: "A social strategy helps you shape how your brand is seen online.",
                  },
                  {
                    title: "Support for other channels",
                    desc: "Social media works best when it supports your website, SEO, and wider marketing efforts.",
                  },
                  {
                    title: "Paid social opportunities",
                    desc: "Campaigns can help extend your reach when the message and offer are clear.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center transition-shadow hover:shadow-medium"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {item.title === "Support for other channels" ? (
                        <>
                          Social media works best when it supports your{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            website
                          </Link>
                          ,{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>
                          , and wider marketing efforts.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              Why Choose Square Trigger for Social Media Marketing
            </h2>
            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              We do not treat social media like a content calendar alone. We
              look at what the business needs to communicate, who it needs to
              reach, and how the content should support trust, visibility, and
              growth.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="mb-2 font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-14"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Capabilities
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              What Our Social Media Marketing Service{" "}
              <span className="gradient-text">Includes</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {capability.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {capability.title === "Content Creation Support" ? (
                        <>
                          Support for branded post copy, creative direction, and
                          campaign-ready content planning.
                        </>
                      ) : capability.title === "Community Presence" ? (
                        <>
                          Support for building a more active and credible{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            brand presence online
                          </Link>
                          .
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Solutions
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              Social Media Support Built Around{" "}
              <span className="gradient-text">Real Business Goals</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {offering.title === "Business-Focused Social Direction" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="leading-relaxed text-background/70">
                  {offering.title === "Business-Focused Social Direction" ? (
                    <>
                      Social media support that works alongside your{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        website
                      </Link>
                      ,{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        SEO
                      </Link>
                      , and broader digital presence.
                    </>
                  ) : (
                    offering.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wider text-primary">
                Deliverables
              </span>

              <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="mx-auto mb-8 max-w-[720px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                Our social media marketing service is scoped around what your
                brand needs to communicate more clearly and more consistently.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8 sm:text-left"
            >
              <ul className="inline-grid space-y-4 text-left sm:grid">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex w-fit items-start gap-3"
                  >
                    <CheckCircle className="mt-[2px] h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-sm leading-relaxed text-foreground/90 sm:text-base">
                      {item === "Brand messaging guidance for social posts" ? (
                        <>
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            Brand messaging
                          </Link>{" "}
                          guidance for social posts
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Process
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              How We Approach{" "}
              <span className="gradient-text">Social Media Marketing</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 flex w-full justify-center">
                    <div className="text-center text-3xl font-display font-bold gradient-text">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.title === "Content Direction" ? (
                      <>
                        We shape what should be posted, how it should sound, and
                        how it should support trust and visibility.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Need Social Media That Supports the Business, Not Just the Feed?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Let&apos;s talk about what your audience needs to see, what your
                brand needs to say, and how social media should support real
                growth.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                Social Media Marketing –{" "}
                <span className="gradient-text">Frequently Asked Questions</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.schemaAnswer,
                  },
                })),
              })}
            </script>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Ready to Build a Stronger Social Media Presence?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let&apos;s create a social media direction that helps your
                business stay visible, communicate clearly, and build stronger
                trust online.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="gradient"
                  size="lg"
                  asChild
                  className="group w-full justify-center sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center"
                  >
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}